/**
 * Payout History Component
 * Displays studio's payout history from studio_payouts table
 * Updated to use real data from revenue system
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../../../utils/supabase';
import Icon from '../../../components/AppIcon';

const PayoutHistory = ({ studioId }) => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [nextPayout, setNextPayout] = useState(null);

  useEffect(() => {
    if (studioId) {
      loadPayoutHistory();
    }
  }, [studioId]);

  const loadPayoutHistory = async () => {
    setLoading(true);

    try {
      // Fetch completed payouts
      const { data: completedPayouts, error: payoutsError } = await supabase
        .from('studio_payouts')
        .select('*')
        .eq('studio_id', studioId)
        .in('status', ['completed', 'processing', 'failed'])
        .order('period_end', { ascending: false })
        .limit(20);

      if (payoutsError) throw payoutsError;

      setPayouts(completedPayouts || []);

      // Fetch next pending payout
      const { data: pendingPayout, error: pendingError } = await supabase
        .from('studio_payouts')
        .select('*')
        .eq('studio_id', studioId)
        .eq('status', 'pending')
        .order('scheduled_payout_date', { ascending: true })
        .limit(1)
        .single();

      if (!pendingError && pendingPayout) {
        setNextPayout(pendingPayout);
      }

    } catch (err) {
      console.error('Error loading payout history:', err);
    } finally {
      setLoading(false);
    }
  };

  const displayedPayouts = showAll ? payouts : payouts.slice(0, 3);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 dark:bg-green-900/30 dark:text-green-300';
      case 'processing':
        return 'text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-300';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'failed':
        return 'text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle';
      case 'processing':
        return 'Loader';
      case 'pending':
        return 'Clock';
      case 'failed':
        return 'XCircle';
      default:
        return 'AlertCircle';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-muted-foreground">Loading payout history...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Payout History</h2>
        {payouts.length > 0 && (
          <button className="flex items-center space-x-2 px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm">
            <Icon name="Download" size={16} />
            <span>Download Statement</span>
          </button>
        )}
      </div>

      {payouts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="DollarSign" size={48} className="mx-auto mb-3 opacity-50" />
          <p className="text-lg font-medium">No payout history yet</p>
          <p className="text-sm mt-1">
            Payouts will appear here once you've earned revenue and meet the minimum threshold
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {displayedPayouts.map((payout) => (
              <div key={payout.id} className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="CreditCard" size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">${payout.payout_amount.toFixed(2)}</div>
                    <div className="text-sm text-muted-foreground">
                      {formatDate(payout.period_start)} - {formatDate(payout.period_end)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {payout.iap_transaction_count} IAP + {payout.ad_impression_count} ads
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payout.status)}`}>
                    <Icon name={getStatusIcon(payout.status)} size={12} />
                    <span className="capitalize">{payout.status}</span>
                  </div>
                  {payout.stripe_transfer_id && (
                    <div className="text-xs text-muted-foreground font-mono">
                      {payout.stripe_transfer_id.slice(0, 12)}...
                    </div>
                  )}
                  {payout.status === 'failed' && payout.failure_reason && (
                    <div className="text-xs text-red-600 max-w-[200px] truncate" title={payout.failure_reason}>
                      {payout.failure_reason}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {payouts.length > 3 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full mt-4 py-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium"
            >
              {showAll ? 'Show Less' : `Show All ${payouts.length} Payouts`}
            </button>
          )}
        </>
      )}

      {nextPayout && (
        <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Info" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">Next Payout</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Your next payout of <span className="font-medium text-foreground">${nextPayout.payout_amount.toFixed(2)}</span> will be processed on{' '}
            <span className="font-medium text-foreground">{formatDate(nextPayout.scheduled_payout_date)}</span> via Stripe.
          </p>
          <div className="text-xs text-muted-foreground mt-2">
            Period: {formatDate(nextPayout.period_start)} - {formatDate(nextPayout.period_end)}
          </div>
        </div>
      )}

      {!nextPayout && payouts.length === 0 && (
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Info" size={16} className="text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Getting Started</span>
          </div>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Set up your payment account to start receiving payouts. Once you earn revenue, payouts are processed monthly with a ${nextPayout?.minimum_payout_amount || 50} minimum threshold.
          </p>
        </div>
      )}
    </div>
  );
};

export default PayoutHistory;
